import React from "react"
import { UserContext } from "../../../context/userContext"
import { useState, useContext, useEffect } from "react"
import { FirebaseContext } from "../../../firebase"
import { useQuery, gql } from "@apollo/client"
import _ from "lodash"
import SelectEnterpriseOrganization from "./components/SelectEnterpriseOrganization"

export default function SelectorContainer() {
  const userCtx = useContext(UserContext)
  const firebase = useContext(FirebaseContext)
  const [enterpriseList, setEnterpriseList] = useState([])
  // Keyed by enterprise_id so the TreeSelect can render enterprise -> organization hierarchy.
  const [enterpriseOrganizations, setEnterpriseOrganizations] = useState({})
  const [fetching, setFetching] = useState(true)

  useEffect(() => {
    const enterpriseDocs = userCtx.session.enterprise_list
    // Only show organizations that the current user is explicitly allowed to access.
    const allowedOrganizations = userCtx.session.organization_list || []

    const unsubscribeEnterprise = _.map(enterpriseDocs, (docRef) =>
      docRef.onSnapshot((docSnapshot) => {
        const enterprise = docSnapshot.data()
        const enterpriseInfo = {
          enterprise_id: docRef.id,
          enterprise_name: _.get(enterprise, "enterprise_name", ""),
          enterprise_status: _.get(enterprise, "enterprise_status", ""),
        }

        setEnterpriseList((prevList) => {
          //Remove old enterprise info from list to handle stale and/or inactive entries
          const updatedList = prevList.filter(
            (item) => item.enterprise_id !== enterpriseInfo.enterprise_id,
          )
          //Add the updated enterprise info back into the list if it is still active
          if (enterpriseInfo.enterprise_status === "ACTIVE") {
            return [...updatedList, enterpriseInfo]
          }
          return updatedList
        })
      }),
    )

    // Subscribe to each enterprise's organizations so we can build one combined tree.
    const unsubscribeOrganization = _.map(enterpriseDocs, (docRef) => {
      const enterpriseId = docRef.id
      const organizationListRef = firebase.db
        .collection("enterprise-config")
        .doc(enterpriseId)
        .collection("organizations")

      return organizationListRef.onSnapshot((snapshot) => {
        const allowedOrganizationList = []
        snapshot.docs.forEach((doc) => {
          if (_.includes(allowedOrganizations, doc.id)) {
            const organization = doc.data()

            if (organization.organization_status === "ACTIVE") {
              allowedOrganizationList.push({
                organization_id: doc.id,
                organization_name: organization.organization_name,
              })
            }
          }
        })

        // Keep each enterprise's org list isolated so snapshots can update independently.
        setEnterpriseOrganizations((prevState) => ({
          ...prevState,
          [enterpriseId]: allowedOrganizationList,
        }))
      })
    })

    setFetching(false)

    return () => {
      // Cleanup the listeners when the component unmounts or when the effect reruns
      unsubscribeEnterprise.forEach((unsubscribe) => unsubscribe())
      unsubscribeOrganization.forEach((unsubscribe) => unsubscribe())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userCtx.session.enterprise_list,
    userCtx.session.organization_list,
    userCtx.refreshTrigger,
  ])

  const ENTERPRISE_BY_USER_QUERY = gql`
    query UserData($staff_id: ID!) {
      getAllEnterprisesByUid(staff_id: $staff_id) {
        enterprise_id
      }
    }
  `
  const { refetch: refetchEnterprises } = useQuery(ENTERPRISE_BY_USER_QUERY, {
    variables: { staff_id: userCtx.session.user.staff_id },
    skip: true,
  })

  useEffect(() => {
    const fetchEnterpriseData = async () => {
      try {
        const { data } = await refetchEnterprises()
        const enterpriseIds = (data?.getAllEnterprisesByUid || []).map(
          (enterprise) => enterprise.enterprise_id,
        )

        if (!enterpriseIds.length) {
          return
        }

        // If current enterprise is no longer in the user's memberships, switch to first valid one.
        if (!enterpriseIds.includes(userCtx.session.user.enterprise_id)) {
          userCtx.changeEnterprise(enterpriseIds[0])
        }
      } catch (error) {
        console.error("Failed to refetch enterprise memberships", error)
      }
    }

    fetchEnterpriseData()
  }, [
    enterpriseList,
    refetchEnterprises,
    userCtx,
    userCtx.session.user.enterprise_id,
  ])

  return (
    <div className="flex flex-row w-full">
      <SelectEnterpriseOrganization
        fetching={fetching}
        enterpriseList={enterpriseList}
        enterpriseOrganizations={enterpriseOrganizations}
      />
    </div>
  )
}